/**
 * Investor Portal (Phase 2 · real backend).
 *
 * Two named exports:
 *  - <InvestorLogin />       →  /investor/login
 *  - <InvestorDashboard />   →  /investor/dashboard
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  Mail,
  TrendingUp,
  ArrowRight,
  ShieldCheck,
  FileText,
  Download,
  LogOut,
  Wallet,
  IndianRupee,
  Activity,
  Info,
  AlertCircle,
  CheckCircle2,
  Plus,
  X,
} from "lucide-react";
import { Investor, investorTokenStorage, API } from "@/lib/api";
import { safeHtml } from "@/lib/sanitize";
import UpiPaymentCard from "@/components/UpiPaymentCard";
import DashboardBrandStrip from "@/components/DashboardBrandStrip";
import LanguageSelector from "@/components/LanguageSelector";
import { useI18n } from "@/lib/i18n";

const RETURN_DISCLAIMER =
  "Indicative target return. Actual returns are subject to market and business risk. Not guaranteed. Please read the offer document carefully before investing.";

/* ---------- shared shell ---------- */
function PortalShell({ children }) {
  return (
    <div className="portal-shell min-h-screen bg-gradient-to-br from-[#04102B] via-[#08194a] to-[#020a1f] text-white">
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 md:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <Link
            to="/"
            data-testid="investor-portal-home-link"
            className="flex items-center gap-2 text-xs sm:text-sm text-white/70 hover:text-[#F0D788] transition min-w-0"
          >
            ← Sahyog Finance &amp; Noble Infinity
          </Link>
          <div className="flex items-center gap-3 shrink-0">
            <LanguageSelector testId="investor-portal-lang" />
            <div className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
              Investor Portal
            </div>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function inr(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

/* =====================================================================
   1 · Investor Login
   ===================================================================== */
export function InvestorLogin() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await Investor.login({ email: email.trim(), password });
      investorTokenStorage.set(r.token);
      nav("/investor/dashboard");
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Invalid credentials. If this is your first login, please use the activation link we emailed you.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalShell>
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
        <section className="space-y-6">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
            Investor Relations
          </div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight">
            Welcome to your <em className="not-italic text-[#F0D788]">Investor Portal</em>
          </h1>
          <p className="text-white/75 leading-relaxed max-w-md">
            Track your portfolio, download digital agreements, and monitor your monthly
            earnings — all in one secure place.
          </p>
          <ul className="space-y-2.5 text-sm text-white/80">
            {[
              "Real-time Portfolio Tracking & Performance Dashboard",
              "Digital Agreement with e-Signature",
              "Monthly Earnings Reports & Instant KYC / Tax downloads",
              "Bank-grade encryption &amp; password reset facility",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="text-[#F0D788] mt-0.5 shrink-0" />
                <span dangerouslySetInnerHTML={safeHtml(f)} />
              </li>
            ))}
          </ul>
          <div className="pt-4 text-[11px] text-white/40 leading-relaxed max-w-md border-l-2 border-[#D4AF37]/40 pl-3">
            <strong className="text-white/60">Disclaimer:</strong> {RETURN_DISCLAIMER}
          </div>
        </section>

        <section
          data-testid="investor-login-card"
          className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-8 md:p-10 backdrop-blur"
        >
          <div className="flex items-center gap-2.5 text-sm text-white/60 mb-1.5">
            <Lock size={14} className="text-[#F0D788]" /> Secure login
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-6">{t("login.title")}</h2>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="investor-email" className="text-[11px] uppercase tracking-wider text-white/55">
                {t("login.field.email")}
              </label>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-4 py-3 focus-within:border-[#D4AF37]/50">
                <Mail size={15} className="text-white/40" />
                <input
                  id="investor-email"
                  data-testid="investor-login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
                />
              </div>
            </div>

            <div>
              <label htmlFor="investor-password" className="text-[11px] uppercase tracking-wider text-white/55">
                {t("login.field.password")}
              </label>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-4 py-3 focus-within:border-[#D4AF37]/50">
                <Lock size={15} className="text-white/40" />
                <input
                  id="investor-password"
                  data-testid="investor-login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="investor-login-error"
                className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm px-3 py-2.5"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="investor-login-submit"
              className="w-full mt-2 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
            >
              {busy ? t("login.submitting") : t("login.submit")}
            </button>

            <div className="flex items-center justify-between text-xs pt-1">
              <Link
                to="/investor/forgot-password"
                data-testid="investor-forgot-password-link"
                className="text-[#F0D788] hover:underline"
              >
                {t("login.forgot")}
              </Link>
              <span className="text-white/40">New investor?</span>
            </div>
          </form>

          <div className="mt-7 pt-5 border-t border-white/10 text-[11px] text-white/45 leading-relaxed">
            <strong className="text-white/60">Note:</strong> Investor accounts are created by our
            Investor Relations team after in-person onboarding &amp; document verification. If you
            haven&apos;t received your activation link, please contact{" "}
            <a href="mailto:info.nobleinfinity@gmail.com" className="underline">
              info.nobleinfinity@gmail.com
            </a>
            .
          </div>
        </section>
      </main>
    </PortalShell>
  );
}

/* =====================================================================
   2 · Investor Dashboard (live data)
   ===================================================================== */
function StatCard({ icon: Icon, label, value, sub, subColor = "text-white/55", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-[#D4AF37]/40 transition"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50">
        <Icon size={13} className="text-[#F0D788]" /> {label}
      </div>
      <div className="mt-3 font-display text-3xl md:text-[32px] leading-none text-white">
        {value}
      </div>
      {sub && <div className={`mt-2 text-xs ${subColor}`}>{sub}</div>}
    </div>
  );
}

export function InvestorDashboard() {
  const nav = useNavigate();
  const [investor, setInvestor] = useState(null);
  const [stats, setStats] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInitiate, setShowInitiate] = useState(false);

  useEffect(() => {
    if (!investorTokenStorage.get()) {
      nav("/investor/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [me, s, inv] = await Promise.all([
          Investor.me(),
          Investor.stats(),
          Investor.investments(),
        ]);
        setInvestor(me);
        setStats(s);
        setInvestments(inv.items || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          nav("/investor/login", { replace: true });
          return;
        }
        setError(
          err?.response?.data?.detail ||
            "Could not load your portfolio. Please try again in a moment.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = () => {
    investorTokenStorage.clear();
    nav("/investor/login");
  };

  const downloadAgreement = (investmentId) => {
    // Bearer-auth GET → use a form-less anchor with fetched blob
    const token = investorTokenStorage.get();
    fetch(`${API}/investor/agreement/${investmentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Download failed");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Sahyog-Investment-Agreement-${investmentId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert("Sorry — the agreement could not be downloaded. Please try again."));
  };

  if (loading) {
    return (
      <PortalShell>
        <main className="max-w-6xl mx-auto px-5 md:px-8 py-24 text-center text-white/60">
          Loading your portfolio…
        </main>
      </PortalShell>
    );
  }

  if (error) {
    return (
      <PortalShell>
        <main className="max-w-3xl mx-auto px-5 md:px-8 py-24">
          <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-6 text-red-200">
            {error}
          </div>
        </main>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-10">
        <DashboardBrandStrip />
        <section className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Welcome back</div>
            <h1
              data-testid="investor-dashboard-greeting"
              className="mt-2 font-serif text-3xl md:text-4xl"
            >
              {investor?.name || "Investor"}
            </h1>
            <div className="mt-2 inline-flex items-center gap-2 text-sm text-white/70">
              <TrendingUp size={15} className="text-emerald-400" />
              <span>
                Portfolio target return{" "}
                <span className="text-[#F0D788] font-semibold">
                  up to {(stats?.target_return_pct ?? 11.55).toFixed(2)}% p.a.
                </span>
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="investor-logout"
            className="inline-flex items-center gap-2 self-start md:self-auto px-5 py-2.5 rounded-full border border-white/15 text-sm text-white/75 hover:text-white hover:border-[#D4AF37]/50"
          >
            <LogOut size={14} /> Sign out
          </button>
        </section>

        <section className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowInitiate(true)}
            data-testid="investor-make-investment"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
          >
            <Plus size={14} /> Make a new investment
          </button>
        </section>

        {/* Eligibility check CTA (public tool investors can share with contacts) */}
        <section
          data-testid="investor-eligibility-cta"
          className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#0A1F44] via-[#0e2a6b] to-[#0A1F44] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#F0D788]">
              Sahyog tool
            </div>
            <div className="mt-1 font-display text-lg text-white">
              Loan Eligibility Check
            </div>
            <div className="text-xs text-white/60 mt-0.5">
              Refer prospects for a ₹1,500 pre-eligibility assessment — verdict emailed within 5-6 working days.
            </div>
          </div>
          <a
            href="/eligibility"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold whitespace-nowrap"
          >
            Check My Loan Eligibility (₹1,500)
          </a>
        </section>

        {(stats?.investment_count ?? 0) === 0 && (
          <div
            data-testid="investor-no-investments-banner"
            className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] px-5 py-4 flex items-start gap-3 text-sm text-[#F0D788]"
          >
            <Info size={17} className="mt-0.5 shrink-0" />
            <div>
              <strong>No investments on file yet.</strong> Your portfolio metrics will appear
              here once our Investor Relations team records your first investment. Contact{" "}
              <a href="mailto:info.nobleinfinity@gmail.com" className="underline">
                info.nobleinfinity@gmail.com
              </a>{" "}
              for status.
            </div>
          </div>
        )}

        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            icon={Wallet}
            label="Total Invested"
            value={`₹${inr(stats?.total_invested)}`}
            sub="Aggregate committed capital"
            testId="investor-stat-invested"
          />
          <StatCard
            icon={IndianRupee}
            label="Current Value"
            value={`₹${inr(stats?.current_value)}`}
            sub={`${stats?.gain_pct >= 0 ? "+" : ""}${(stats?.gain_pct ?? 0).toFixed(2)}% since investment`}
            subColor={stats?.gain_pct >= 0 ? "text-emerald-400" : "text-red-300"}
            testId="investor-stat-value"
          />
          <StatCard
            icon={Activity}
            label="Monthly Return (est.)"
            value={`₹${inr(stats?.monthly_return)}`}
            sub="Based on target return rate"
            subColor="text-emerald-400"
            testId="investor-stat-monthly"
          />
        </section>

        {investments.length > 0 && (
          <section>
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Documents</div>
                <h2 className="mt-1.5 font-serif text-2xl">Investment Agreements</h2>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.05] text-white/55 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left p-4">Agreement</th>
                    <th className="text-left p-4 hidden md:table-cell">Principal</th>
                    <th className="text-left p-4 hidden md:table-cell">Invested</th>
                    <th className="text-left p-4">Current Value</th>
                    <th className="text-right p-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((inv) => (
                    <tr key={inv.id} className="border-t border-white/5">
                      <td className="p-4 text-white/85 flex items-center gap-2.5">
                        <FileText size={15} className="text-[#F0D788]" />
                        <span className="font-mono text-xs">{inv.agreement_id}</span>
                      </td>
                      <td className="p-4 hidden md:table-cell text-white/70">
                        ₹{inr(inv.principal_amount)}
                      </td>
                      <td className="p-4 hidden md:table-cell text-white/55">
                        {inv.invested_at?.slice(0, 10)}
                      </td>
                      <td className="p-4 text-emerald-300 font-medium">
                        ₹{inr(inv.current_value)}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => downloadAgreement(inv.id)}
                          data-testid={`investor-download-${inv.id}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#D4AF37]/40 text-[#F0D788] text-xs hover:bg-[#D4AF37]/10"
                        >
                          <Download size={13} /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-white/45 flex items-start gap-1.5">
              <CheckCircle2 size={12} className="mt-0.5 text-emerald-400 shrink-0" />
              KYC bundle &amp; TDS certificates will appear here in a future update.
            </p>
          </section>
        )}

        <section className="rounded-2xl border border-white/10 bg-black/20 p-5 text-[11px] text-white/50 leading-relaxed">
          <strong className="text-white/70">Disclaimer:</strong> {RETURN_DISCLAIMER} Portfolio
          current-value figures are computed on a simple-interest basis at the indicative target
          rate and are for reference only. Final settlement is subject to actual business
          performance and the terms of your investment agreement.
        </section>
      </main>

      {showInitiate && investor && (
        <InitiateInvestmentModal
          investor={investor}
          onClose={() => setShowInitiate(false)}
          onDone={() => {
            setShowInitiate(false);
            // reload dashboard
            Investor.investments()
              .then((r) => setInvestments(r.items || []))
              .catch(() => {});
          }}
        />
      )}
    </PortalShell>
  );
}

/* =====================================================================
   Initiate Investment Modal — investor-side UPI flow
   ===================================================================== */
function InitiateInvestmentModal({ investor, onClose, onDone }) {
  const [step, setStep] = useState("form"); // form | pay | done
  const [form, setForm] = useState({ principal_amount: "", tenure_months: 36, notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [investment, setInvestment] = useState(null);
  const [payment, setPayment] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = Number(form.principal_amount);
    if (!amt || amt < 1000) {
      setError("Minimum investment amount is ₹1,000.");
      return;
    }
    setBusy(true);
    try {
      const r = await Investor.initiate({
        principal_amount: amt,
        tenure_months: Number(form.tenure_months) || 36,
        notes: form.notes,
      });
      setInvestment(r.investment);
      setStep("pay");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not initiate.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[#0A1F44] rounded-3xl border border-[#D4AF37]/30 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0A1F44] flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="font-display text-lg text-white">
            {step === "form" && "New investment"}
            {step === "pay" && "Pay & upload proof"}
            {step === "done" && "Submitted"}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          {step === "form" && (
            <form onSubmit={submit} data-testid="investor-initiate-form" className="space-y-4">
              <p className="text-white/60 text-xs leading-relaxed">
                Enter your investment amount. On the next step, you&apos;ll pay via UPI/Bank
                Transfer and upload the payment proof. Your investment will be activated within
                1 business day after admin verification.
              </p>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
                  Principal amount (₹) <span className="text-[#D4AF37]">*</span>
                </span>
                <div className="relative">
                  <IndianRupee size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="initiate-amount"
                    required
                    type="number"
                    min="1000"
                    step="1000"
                    value={form.principal_amount}
                    onChange={(e) => setForm((f) => ({ ...f, principal_amount: e.target.value }))}
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white"
                    placeholder="500000"
                  />
                </div>
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
                  Tenure (months)
                </span>
                <input
                  data-testid="initiate-tenure"
                  type="number"
                  min="1"
                  max="600"
                  value={form.tenure_months}
                  onChange={(e) => setForm((f) => ({ ...f, tenure_months: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
                  Notes (optional)
                </span>
                <textarea
                  data-testid="initiate-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white"
                  placeholder="Bank reference, purpose, etc."
                />
              </label>
              <div className="rounded-xl bg-[#D4AF37]/[0.06] border border-[#D4AF37]/25 px-4 py-3 text-[11px] text-white/65 leading-relaxed">
                <strong className="text-[#F0D788]">Target return:</strong>{" "}
                up to {(investor.target_return_pct ?? 11.55).toFixed(2)}% p.a.<br />
                Actual returns are subject to market and business risk. Not guaranteed.
              </div>
              {error && (
                <div className="text-rose-200 text-xs px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
                  {error}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="initiate-continue"
                  className="px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? "Working…" : "Continue to payment"}
                </button>
              </div>
            </form>
          )}

          {step === "pay" && investment && !payment && (
            <UpiPaymentCard
              kind="investment"
              targetId={investment.id}
              amount={investment.principal_amount}
              payerName={investor.name}
              payerEmail={investor.email}
              payerPhone={investor.phone || ""}
              testIdPrefix="initiate-upi"
              onSubmitted={(res) => {
                setPayment(res);
                setStep("done");
              }}
            />
          )}

          {step === "done" && (
            <div data-testid="initiate-done" className="text-center py-4">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
              <h4 className="mt-5 font-serif text-2xl text-white">Payment proof submitted!</h4>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">
                Your investment is{" "}
                <strong className="text-emerald-300">Pending Verification</strong>. On admin
                approval you&apos;ll receive the signed digital investment agreement at{" "}
                <strong className="text-white">{investor.email}</strong>.
              </p>
              <div className="mt-4 text-[11px] text-white/60">
                Reference:{" "}
                <span className="font-mono text-[#F0D788]">
                  {(payment?.payment_id || "").slice(0, 8).toUpperCase()}
                </span>
              </div>
              <button
                onClick={onDone}
                data-testid="initiate-close"
                className="mt-6 px-6 py-2.5 rounded-full btn-gold text-sm font-semibold"
              >
                Back to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvestorLogin;
